import { Routes, Route } from 'react-router-dom'
import { lazy, Suspense, useState, useEffect } from 'react'
import { useAuthStore } from './store/authStore'
import { useLibraryStore } from './store/libraryStore'
import { supabase } from './api/supabase'
import { Loader2 } from 'lucide-react'
import { useChapterNotify } from './hooks/useChapterNotify'

// Static imports for auth/onboarding — needed immediately on first render
import AuthModal from './components/layout/AuthModal'
import OnboardingModal from './components/layout/OnboardingModal'
import LandingPage from './components/layout/LandingPage'

// Lazy-load all pages — Vite splits each into its own chunk
const Layout        = lazy(() => import('./components/layout/Layout'))
const Home          = lazy(() => import('./pages/Home'))
const Manga         = lazy(() => import('./pages/Manga'))
const Anime         = lazy(() => import('./pages/Anime'))
const MangaDetail   = lazy(() => import('./pages/MangaDetail'))
const AnimeDetail   = lazy(() => import('./pages/AnimeDetail'))
const MangaReader   = lazy(() => import('./pages/MangaReader'))
const Library       = lazy(() => import('./pages/Library'))
const Search        = lazy(() => import('./pages/Search'))
const Requests      = lazy(() => import('./pages/Requests'))
const AdminDashboard= lazy(() => import('./pages/AdminDashboard'))
const Profile       = lazy(() => import('./pages/Profile'))
const UserProfile   = lazy(() => import('./pages/UserProfile'))
const Messages      = lazy(() => import('./pages/Messages'))
const ForYou        = lazy(() => import('./pages/ForYou'))
const AIScout       = lazy(() => import('./pages/AIScout'))
const Community     = lazy(() => import('./pages/Community'))
const Notifications = lazy(() => import('./pages/Notifications'))
const Digest        = lazy(() => import('./pages/Digest'))
const Upgrade       = lazy(() => import('./pages/Upgrade'))
const Stats         = lazy(() => import('./pages/Stats'))
const Collections   = lazy(() => import('./pages/Collections'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))

const PageLoader = () => (
  <div className="flex items-center justify-center py-24">
    <Loader2 size={20} className="animate-spin text-accent/60" />
  </div>
)

export default function App() {
  const { user, profile, setUser, setSession, setProfile, setSubscription } = useAuthStore()
  const [authReady, setAuthReady] = useState(false)
  useChapterNotify()

  // Request persistent storage on load if user has downloads — prevents OS auto-eviction
  useEffect(() => {
    try {
      const meta = JSON.parse(localStorage.getItem('nakama-downloads') || '{}')
      if (Object.keys(meta).length > 0) navigator.storage?.persist?.()
    } catch {}
  }, [])

  useEffect(() => {
    const loadProfile = async (userId) => {
      if (!userId) { setProfile(null); return }
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()
      setProfile(data ?? null)
    }

    const loadSubscription = async (userId) => {
      if (!userId) { setSubscription(null); return }
      const { data } = await supabase
        .from('subscriptions')
        .select('status, plan, current_period_end')
        .eq('user_id', userId)
        .maybeSingle()
      setSubscription(data ?? null)
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      loadProfile(session?.user?.id ?? null)
      loadSubscription(session?.user?.id ?? null)
      setAuthReady(true)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      loadProfile(session?.user?.id ?? null)
      loadSubscription(session?.user?.id ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  // Splash screen while checking session
  if (!authReady) return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-bg3 border border-accent/30 flex items-center justify-center">
          <span className="text-accent font-jp font-bold text-2xl" style={{ textShadow: '0 0 12px rgba(56,189,248,0.6)' }}>仲</span>
        </div>
        <Loader2 size={20} className="animate-spin text-accent/60" />
      </div>
    </div>
  )

  // Password reset — must bypass auth wall so email link works
  if (window.location.pathname === '/reset-password') return (
    <Suspense fallback={<PageLoader />}>
      <ResetPassword />
    </Suspense>
  )

  // Landing page for non-authenticated users
  if (!user) return <LandingPage />

  // Onboarding — shown after first sign-up, can't be skipped entirely
  if (user && profile && !profile.onboarding_done) return (
    <div className="min-h-screen bg-bg">
      <OnboardingModal onComplete={() => {}} />
    </div>
  )

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/read/:mangaId/:chapterId" element={<MangaReader />} />
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/for-you" element={<ForYou />} />
          <Route path="/manga" element={<Manga />} />
          <Route path="/manga/:id" element={<MangaDetail />} />
          <Route path="/anime" element={<Anime />} />
          <Route path="/anime/:id" element={<AnimeDetail />} />
          <Route path="/library" element={<Library />} />
          <Route path="/search" element={<Search />} />
          <Route path="/requests" element={<Requests />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/:userId" element={<UserProfile />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/scout" element={<AIScout />} />
          <Route path="/community" element={<Community />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/digest" element={<Digest />} />
          <Route path="/upgrade" element={<Upgrade />} />
          <Route path="/upgrade/success" element={<Upgrade success />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/collections" element={<Collections />} />
          <Route path="*" element={
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <p className="font-display text-6xl text-gray-700 mb-4">404</p>
              <p className="text-gray-400 mb-6">This page doesn't exist.</p>
              <a href="/" className="px-5 py-2.5 bg-accent text-bg font-bold rounded-xl text-sm hover:opacity-90 transition-all">Go home</a>
            </div>
          } />
        </Route>
      </Routes>
    </Suspense>
  )
}
